import { useCallback, useEffect, useMemo, useState } from 'react'
import { cn } from '~utils/cn'

import { useNavigate } from '@tanstack/react-router'
import CreatePetDialog from '~components/dialog/create-pet-dialog'
import PetCard from '~components/general/pet-card'
import Icon from '~components/ui/icon'
import Spinner from '~components/ui/loading-indicator/spinner'
import ConsultationLayout from '~containers/app/ask-a-vet/components/layout'
import { ConsultationData } from '~containers/app/ask-a-vet/types'
import Pet from '~models/pet'
import useMyPets from '~repositories/hooks/use-my-pets'
import getRootPath from '~utils/app/get-root-path'
import UpdatePetBottomSheet from './components/update-pet.bottomsheet'
import { useConsultationData } from './store/consultation-data'

const MyPetsLoading = () => (
  <div className="flex-1 flex justify-center items-center">
    <Spinner />
  </div>
)

const MyPets = () => {
  const [isPetDialogOpen, setPetDialogOpen] = useState(false)
  const [{ pet_id: selectedPetId }, setConsultationData] = useConsultationData()
  const { data: myPets, isFetching } = useMyPets()

  const [toUpdatePet, setToUpdatePet] = useState<Pet.ILevel1 | null>(null)

  const nonDraftPets = useMemo(() => {
    return myPets?.filter(pet => !Pet.isIncomplete(pet))
  }, [myPets])

  const onPetSelect = useCallback(
    (pet: Pet.ILevel1 | null) => {
      if (pet && pet.id !== selectedPetId) {
        if (Pet.isIncomplete(pet)) {
          console.log('incomplete pet', pet)
          setToUpdatePet(pet)
        } else {
          console.log('complete pet', pet)
          // 펫이 바뀌면 이전 펫의 문진(답·응급 안내 단계·미리 채운 몸무게·상세 입력)을 넘기지 않고 새로 시작한다
          setConsultationData(new ConsultationData({ pet, pet_id: pet.id }))
        }
      }
    },
    [setConsultationData, selectedPetId]
  )

  const openPetDialog = useCallback(() => setPetDialogOpen(true), [])
  const closePetDialog = useCallback(() => {
    setPetDialogOpen(false)
  }, [])

  useEffect(() => {
    if (myPets && myPets.length <= 0) {
      openPetDialog()
    }
    if (!selectedPetId && nonDraftPets && nonDraftPets.length > 0) {
      onPetSelect(nonDraftPets[0])
    }
  }, [myPets, onPetSelect, openPetDialog, selectedPetId])

  if (isFetching) {
    return <MyPetsLoading />
  }

  return (
    <>
      <div className="my-2 flex flex-col flex-gap-y-2 overflow-y-auto">
        {myPets?.map(pet => {
          const isSelected = !Pet.isIncomplete(pet) && selectedPetId === pet.id
          const isIncomplete = Pet.isIncomplete(pet)

          return (
            <PetCard
              key={pet.id}
              pet={pet}
              onClick={() => {
                onPetSelect(pet)
              }}
              className={cn(
                'flex cursor-pointer justify-between gap-4',
                isSelected && 'bg-white dark:bg-gray-800'
              )}
            >
              <div className="flex min-w-0 flex-gap-x-4">
                <PetCard.Avatar
                  className={isSelected ? 'dark:border-gray-200' : 'dark:border-gray-600'}
                />
                <div className="min-w-0">
                  <PetCard.Name />
                  {!isIncomplete && <PetCard.Kind />}
                  {!isIncomplete && <PetCard.AgeWithGender />}
                  {isIncomplete ? <PetCard.Draft /> : null}
                </div>
              </div>
              <Icon
                name="check_mark"
                className={cn(
                  'size-6 shrink-0 fill-gray-300 dark:fill-gray-600',
                  isSelected && 'fill-primary-600 dark:fill-extended-error'
                )}
              />
            </PetCard>
          )
        })}
      </div>
      <button
        type="button"
        className="flex items-center flex-gap-x-2 px-2 py-4 text-primary-500 dark:text-primary-400 outline-primary-500 text-title-3 bg-transparent"
        onClick={openPetDialog}
      >
        <Icon name="add_circle_solid" className="h-5 w-5 fill-primary-500 dark:fill-primary-400" />
        Add a new pet
      </button>
      <CreatePetDialog location="askavet" open={isPetDialogOpen} onRequestClose={closePetDialog} />
      <UpdatePetBottomSheet
        isOpen={!!toUpdatePet}
        onCloseRequest={() => setToUpdatePet(null)}
        pet={toUpdatePet}
        onPetUpdateSuccess={onPetSelect}
      />
    </>
  )
}

const SelectPet = () => {
  const navigate = useNavigate()
  const [{ pet_id: selectedPetId }, setConsultationData] = useConsultationData()

  const onNextPress = useCallback(() => {
    // 펫 선택에서 시작하는 상담은 항상 새 문진이다: 제출하지 않은 이전 상담의 증상·답·'모름'·미리 채운 값·
    // 응급 안내 단계·상세 글과 사진이 남지 않게 고른 펫만 두고 임시 저장을 비운다 (QA round 2)
    setConsultationData(prev => new ConsultationData({ pet: prev.pet, pet_id: prev.pet_id }))
    navigate({
      to: `${getRootPath()}/ask-a-vet/main-concern`,
      resetScroll: true,
    })
  }, [navigate, setConsultationData])

  return (
    <ConsultationLayout
      title={`Which pet are you\nasking about?`}
      nextButtonDisabled={!selectedPetId}
      onNextPress={onNextPress}
      required
    >
      <MyPets />
    </ConsultationLayout>
  )
}

export default SelectPet
